/**
 * Leviathan Web - Order Form Component
 * Quasar Prime LLC
 * Reusable form for creating and editing orders
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import { API_URL } from '@/config/api';

interface Customer {
  id: string;
  name: string;
  customerNumber: string;
  deliveryAddress: string | null;
  deliveryCity: string | null;
  deliveryState: string | null;
  deliveryZip: string | null;
  paymentMethod: string | null;
  creditTerms: number | null;
}

interface Product {
  id: string;
  name: string;
  sku: string;
  category: string;
  unit: string;
  pricePerUnit: number | null;
}

interface OrderItemData {
  id?: string;
  productId: string;
  productName?: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  lineTotal: number;
  notes: string;
}

interface OrderFormData {
  customerId: string;
  orderType: 'DELIVERY' | 'PICKUP' | 'WILL_CALL' | 'EMERGENCY';
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  requestedDate: string;
  deliveryAddress: string;
  deliveryCity: string;
  deliveryState: string;
  deliveryZip: string;
  deliveryInstructions: string;
  taxRate: number;
  deliveryFee: number;
  discount: number;
  paymentTerms: string;
  poNumber: string;
  notes: string;
  internalNotes: string;
}

interface OrderFormProps {
  orderId?: string;
  initialData?: Partial<OrderFormData>;
  initialItems?: OrderItemData[];
  onSuccess?: () => void;
}

export default function OrderForm({
  orderId,
  initialData,
  initialItems,
  onSuccess,
}: OrderFormProps) {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const [formData, setFormData] = useState<OrderFormData>({
    customerId: '',
    orderType: 'DELIVERY',
    priority: 'NORMAL',
    requestedDate: '',
    deliveryAddress: '',
    deliveryCity: '',
    deliveryState: '',
    deliveryZip: '',
    deliveryInstructions: '',
    taxRate: 0,
    deliveryFee: 0,
    discount: 0,
    paymentTerms: '',
    poNumber: '',
    notes: '',
    internalNotes: '',
    ...initialData,
  });

  const [items, setItems] = useState<OrderItemData[]>(initialItems || []);

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
  }, []);

  useEffect(() => {
    // When customer changes, auto-fill delivery address
    if (selectedCustomer && !orderId) {
      setFormData((prev) => ({
        ...prev,
        deliveryAddress: selectedCustomer.deliveryAddress || '',
        deliveryCity: selectedCustomer.deliveryCity || '',
        deliveryState: selectedCustomer.deliveryState || '',
        deliveryZip: selectedCustomer.deliveryZip || '',
        paymentTerms: selectedCustomer.creditTerms
          ? `NET ${selectedCustomer.creditTerms}`
          : selectedCustomer.paymentMethod || '',
      }));
    }
  }, [selectedCustomer, orderId]);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers?status=ACTIVE`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
      }
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory?isActive=true`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.items || []);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'customerId') {
      const customer = customers.find((c) => c.id === value);
      setSelectedCustomer(customer || null);
    }
  };

  const handleAddItem = () => {
    setItems((prev) => [
      ...prev,
      {
        productId: '',
        quantity: 0,
        unit: 'gallon',
        unitPrice: 0,
        lineTotal: 0,
        notes: '',
      },
    ]);
  };

  const handleRemoveItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, field: string, value: string | number) => {
    setItems((prev) => {
      const updated = [...prev];
      const existing = updated[index];
      if (!existing) return prev;

      // Create a new item with all required fields explicitly set
      const item: OrderItemData = {
        id: existing.id,
        productId: existing.productId,
        productName: existing.productName,
        quantity: existing.quantity,
        unit: existing.unit,
        unitPrice: existing.unitPrice,
        lineTotal: existing.lineTotal,
        notes: existing.notes,
      };

      if (field === 'productId') {
        const product = products.find((p) => p.id === value);
        if (product) {
          item.productId = value as string;
          item.productName = product.name;
          item.unit = product.unit;
          item.unitPrice = product.pricePerUnit || 0;
          item.lineTotal = (item.quantity || 0) * item.unitPrice;
        }
      } else if (field === 'quantity') {
        item.quantity = Number(value) || 0;
        item.lineTotal = item.quantity * (item.unitPrice || 0);
      } else if (field === 'unitPrice') {
        item.unitPrice = Number(value) || 0;
        item.lineTotal = (item.quantity || 0) * item.unitPrice;
      } else if (field === 'notes') {
        item.notes = String(value);
      }

      updated[index] = item;
      return updated;
    });
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + item.lineTotal, 0);
    const taxAmount = subtotal * (formData.taxRate / 100);
    const total = subtotal + taxAmount + formData.deliveryFee - formData.discount;
    return { subtotal, taxAmount, total };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (!formData.customerId) {
        throw new Error('Please select a customer');
      }

      if (items.length === 0) {
        throw new Error('Please add at least one item');
      }

      const invalidItems = items.filter((item) => !item.productId || item.quantity <= 0);
      if (invalidItems.length > 0) {
        throw new Error('All items must have a product and quantity');
      }

      const payload = {
        ...formData,
        taxRate: Number(formData.taxRate) || 0,
        deliveryFee: Number(formData.deliveryFee) || 0,
        discount: Number(formData.discount) || 0,
        requestedDate: formData.requestedDate || null,
        items: items.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          notes: item.notes || null,
        })),
      };

      const url = orderId ? `${API_URL}/api/orders/${orderId}` : `${API_URL}/api/orders`;

      const response = await fetch(url, {
        method: orderId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/orders');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save order');
    } finally {
      setIsLoading(false);
    }
  };

  const { subtotal, taxAmount, total } = calculateTotals();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-4 bg-error-red/10 border border-error-red/50">
          <p className="text-error-red text-sm font-mono">&gt; Error: {error}</p>
        </div>
      )}

      {/* Customer & Order Info */}
      <FormSection title="Order Information">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-2">
            <FormField label="Customer" required>
              <select
                name="customerId"
                value={formData.customerId}
                onChange={handleChange}
                required
                className="form-input"
              >
                <option value="">-- Select Customer --</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name} ({customer.customerNumber})
                  </option>
                ))}
              </select>
            </FormField>
          </div>

          <FormField label="Order Type">
            <select
              name="orderType"
              value={formData.orderType}
              onChange={handleChange}
              className="form-input"
            >
              <option value="DELIVERY">Delivery</option>
              <option value="PICKUP">Pickup</option>
              <option value="WILL_CALL">Will Call</option>
              <option value="EMERGENCY">Emergency</option>
            </select>
          </FormField>

          <FormField label="Priority">
            <select
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              className="form-input"
            >
              <option value="LOW">Low</option>
              <option value="NORMAL">Normal</option>
              <option value="HIGH">High</option>
              <option value="URGENT">Urgent</option>
            </select>
          </FormField>

          <FormField label="Requested Date">
            <input
              type="date"
              name="requestedDate"
              value={formData.requestedDate}
              onChange={handleChange}
              className="form-input"
            />
          </FormField>

          <FormField label="PO Number">
            <input
              type="text"
              name="poNumber"
              value={formData.poNumber}
              onChange={handleChange}
              placeholder="Customer PO #"
              className="form-input"
            />
          </FormField>

          <FormField label="Payment Terms">
            <input
              type="text"
              name="paymentTerms"
              value={formData.paymentTerms}
              onChange={handleChange}
              placeholder="NET 30, COD, etc."
              className="form-input"
            />
          </FormField>
        </div>
      </FormSection>

      {/* Delivery Address */}
      {formData.orderType === 'DELIVERY' && (
        <FormSection title="Delivery Address">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <FormField label="Street Address">
                <input
                  type="text"
                  name="deliveryAddress"
                  value={formData.deliveryAddress}
                  onChange={handleChange}
                  placeholder="Street address"
                  className="form-input"
                />
              </FormField>
            </div>
            <FormField label="City">
              <input
                type="text"
                name="deliveryCity"
                value={formData.deliveryCity}
                onChange={handleChange}
                className="form-input"
              />
            </FormField>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="State">
                <input
                  type="text"
                  name="deliveryState"
                  value={formData.deliveryState}
                  onChange={handleChange}
                  className="form-input"
                />
              </FormField>
              <FormField label="ZIP">
                <input
                  type="text"
                  name="deliveryZip"
                  value={formData.deliveryZip}
                  onChange={handleChange}
                  className="form-input"
                />
              </FormField>
            </div>
            <div className="md:col-span-2">
              <FormField label="Delivery Instructions">
                <textarea
                  name="deliveryInstructions"
                  value={formData.deliveryInstructions}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Special delivery instructions..."
                  className="form-input resize-none"
                />
              </FormField>
            </div>
          </div>
        </FormSection>
      )}

      {/* Order Items */}
      <FormSection title="Order Items">
        <div className="space-y-4">
          {items.length === 0 ? (
            <div className="text-center py-8 text-text-tertiary font-mono">
              No items added yet. Click "Add Item" to start.
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="grid grid-cols-12 gap-4 p-4 bg-background border border-panel-border items-end"
                >
                  <div className="col-span-4">
                    <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                      Product
                    </label>
                    <select
                      value={item.productId}
                      onChange={(e) => handleItemChange(index, 'productId', e.target.value)}
                      className="form-input"
                    >
                      <option value="">-- Select Product --</option>
                      {products.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.name} ({product.sku})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                      Quantity
                    </label>
                    <input
                      type="number"
                      value={item.quantity || ''}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      step="0.01"
                      min="0"
                      className="form-input"
                    />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                      Unit
                    </label>
                    <input
                      type="text"
                      value={item.unit}
                      readOnly
                      className="form-input bg-panel-border"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                      Unit Price
                    </label>
                    <input
                      type="number"
                      value={item.unitPrice || ''}
                      onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                      step="0.0001"
                      min="0"
                      className="form-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                      Line Total
                    </label>
                    <div className="form-input bg-panel-border text-amber font-semibold">
                      ${item.lineTotal.toFixed(2)}
                    </div>
                  </div>
                  <div className="col-span-1">
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(index)}
                      className="w-full py-2 bg-error-red/20 hover:bg-error-red/30 text-error-red font-mono text-sm transition-all border border-error-red/30"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={handleAddItem}
            className="w-full py-3 bg-steel/20 hover:bg-steel/30 text-steel font-mono uppercase text-sm tracking-wider transition-all border border-steel/30 hover:border-steel"
          >
            + Add Item
          </button>
        </div>
      </FormSection>

      {/* Pricing */}
      <FormSection title="Pricing">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <FormField label="Tax Rate (%)">
              <input
                type="number"
                name="taxRate"
                value={formData.taxRate}
                onChange={handleChange}
                step="0.01"
                min="0"
                className="form-input"
              />
            </FormField>
            <FormField label="Delivery Fee ($)">
              <input
                type="number"
                name="deliveryFee"
                value={formData.deliveryFee}
                onChange={handleChange}
                step="0.01"
                min="0"
                className="form-input"
              />
            </FormField>
            <FormField label="Discount ($)">
              <input
                type="number"
                name="discount"
                value={formData.discount}
                onChange={handleChange}
                step="0.01"
                min="0"
                className="form-input"
              />
            </FormField>
          </div>

          <div className="bg-background border border-panel-border p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-panel-border pb-3">
              <span className="text-text-secondary font-mono text-sm uppercase">Subtotal</span>
              <span className="text-text-primary font-mono">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center border-b border-panel-border pb-3">
              <span className="text-text-secondary font-mono text-sm uppercase">
                Tax ({formData.taxRate}%)
              </span>
              <span className="text-text-primary font-mono">${taxAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center border-b border-panel-border pb-3">
              <span className="text-text-secondary font-mono text-sm uppercase">Delivery Fee</span>
              <span className="text-text-primary font-mono">${formData.deliveryFee.toFixed(2)}</span>
            </div>
            {formData.discount > 0 && (
              <div className="flex justify-between items-center border-b border-panel-border pb-3">
                <span className="text-text-secondary font-mono text-sm uppercase">Discount</span>
                <span className="text-error-red font-mono">-${formData.discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between items-center pt-2">
              <span className="text-text-primary font-mono text-lg uppercase font-bold">Total</span>
              <span
                className="text-amber font-mono text-2xl font-bold"
                style={{ textShadow: '0 0 10px rgba(255, 149, 0, 0.4)' }}
              >
                ${total.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </FormSection>

      {/* Notes */}
      <FormSection title="Notes">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Customer Notes">
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              placeholder="Notes visible to customer..."
              className="form-input resize-none"
            />
          </FormField>
          <FormField label="Internal Notes">
            <textarea
              name="internalNotes"
              value={formData.internalNotes}
              onChange={handleChange}
              rows={3}
              placeholder="Internal notes (not visible to customer)..."
              className="form-input resize-none"
            />
          </FormField>
        </div>
      </FormSection>

      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-4 pt-4">
        <button
          type="button"
          onClick={() => navigate('/orders')}
          className="px-6 py-3 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all border border-panel-border hover:border-steel"
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={isLoading}
          className="px-8 py-3 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed relative group"
          style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
        >
          <span className="relative z-10">
            {isLoading ? 'Saving...' : orderId ? 'Update Order' : 'Create Order'}
          </span>
          <div
            className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ mixBlendMode: 'overlay' }}
          />
        </button>
      </div>
    </form>
  );
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <div className="flex items-center space-x-2 mb-6">
          <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
          <h3 className="text-xl font-display text-text-primary uppercase tracking-wide">
            {title}
          </h3>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
        {label} {required && <span className="text-amber">*</span>}
      </label>
      {children}
    </div>
  );
}
